"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateFinRevenueMappingSchema = exports.createFinRevenueMappingSchema = exports.finRevenueMappingShape = exports.updateFinPostingProfileSchema = exports.createFinPostingProfileSchema = exports.finPostingProfileShape = exports.updateFinPostingGroupSchema = exports.createFinPostingGroupSchema = exports.finPostingGroupShape = exports.systemPostingGroupCodes = exports.updateFinAccountSchema = exports.createFinAccountSchema = exports.finAccountShape = exports.finAccountTypes = void 0;
/**
 * ADR-081 Phase 1 — Finance Masters contracts.
 *
 * DTO-FIRST, AND NO PRISMA TYPE CROSSES THIS BOUNDARY (ADR-062 §9). Every shape
 * here is declared in Zod and the TypeScript types are `z.infer`red from it, so
 * the validator and the type cannot disagree — a hand-written interface beside a
 * schema is two sources of truth that drift silently.
 *
 * WHAT THESE FOUR MASTERS ARE, IN ONE PLACE, BECAUSE THE NAMES ARE EASY TO
 * CONFUSE:
 *
 *   FinAccount         a LEDGER. The only object here that holds money.
 *   FinPostingGroup    a BUSINESS CONCEPT — what kind of revenue this is.
 *   FinPostingProfile  ACCOUNTING POLICY — how that kind posts, plus every
 *                      bill-scoped account (receivable, round-off, advance …).
 *   FinRevenueMapping  the group x profile CELL — the actual ledger choice.
 *
 * Revenue Centre and Cost Centre appear nowhere in this file. They are reporting
 * and expense-allocation dimensions and are never ledgers (ADR-081 D-5).
 */
const zod_1 = require("zod");
const uuid = zod_1.z.string().uuid();
/**
 * Effective-dated windows are ISO date strings on the wire, not `Date`.
 *
 * A parsed `Date` in a contract type is a trap: the wire carries a string, so
 * the inferred type and the JSON disagree the moment anyone reads it. This
 * already cost a defect in `SearchPatientRequest`.
 */
const isoDate = zod_1.z.string().datetime({ offset: true }).or(zod_1.z.string().date());
// ---------------------------------------------------------------------------
// Chart of Accounts — closes audit finding F-9 (it had ONE GET and no writes,
// so a hospital could not create a ledger through the product at all).
// ---------------------------------------------------------------------------
exports.finAccountTypes = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];
exports.finAccountShape = {
    accountCode: zod_1.z.string().min(1).max(20),
    name: zod_1.z.string().min(1).max(255),
    accountType: zod_1.z.enum(exports.finAccountTypes),
    parentAccountId: uuid.nullish(),
    /**
     * A group/header account cannot receive postings. This is the flag the whole
     * "posting to summary accounts" validation rests on, and it defaults TRUE
     * because a leaf account is the ordinary case.
     */
    isPostable: zod_1.z.boolean().optional(),
    /**
     * System accounts (Suspense, Round Off) accept auto-posted entries but reject
     * manual journals — a distinct question from `isActive`/`isPostable`.
     */
    allowManualPosting: zod_1.z.boolean().optional(),
};
exports.createFinAccountSchema = zod_1.z.object(exports.finAccountShape);
exports.updateFinAccountSchema = zod_1.z.object(exports.finAccountShape).partial();
// ---------------------------------------------------------------------------
// Posting Group — the accounting bridge (ADR-081 D-1).
// ---------------------------------------------------------------------------
/**
 * The eleven system groups. Revenue KINDS, never specialties.
 *
 * `CARDIOLOGY`, `ORTHOPAEDICS` and the like are deliberately absent: departments
 * reorganise for clinical reasons and a chart of accounts must not move when they
 * do. Nothing prevents a hospital creating its own group — this list is what is
 * seeded, not what is permitted.
 */
exports.systemPostingGroupCodes = [
    'CONSULTATION',
    'LABORATORY',
    'RADIOLOGY',
    'PROCEDURE',
    'PHARMACY',
    'PACKAGE',
    'DAYCARE',
    'EMERGENCY',
    'WARD',
    'OT',
    'MISC',
];
exports.finPostingGroupShape = {
    code: zod_1.z
        .string()
        .min(1)
        .max(30)
        .regex(/^[A-Z0-9_]+$/, 'Use upper-case letters, digits and underscores'),
    name: zod_1.z.string().min(1).max(255),
};
exports.createFinPostingGroupSchema = zod_1.z.object(exports.finPostingGroupShape);
exports.updateFinPostingGroupSchema = zod_1.z.object(exports.finPostingGroupShape).partial();
// ---------------------------------------------------------------------------
// Posting Profile — accounting policy + every bill-scoped account (D-1a, D-6).
// ---------------------------------------------------------------------------
/**
 * Every account is OPTIONAL.
 *
 * A profile with no accounts is legitimate and is exactly what Phase 1 needs: it
 * falls through to the journal template default, which is today's behaviour. If
 * these were required, the migration could not seed a profile before the
 * accounts existed, and a hospital could not create a profile incrementally.
 */
exports.finPostingProfileShape = {
    code: zod_1.z
        .string()
        .min(1)
        .max(30)
        .regex(/^[A-Z0-9_]+$/, 'Use upper-case letters, digits and underscores'),
    name: zod_1.z.string().min(1).max(255),
    // Bill-scoped: one per BILL, not per posting group (ADR-081 D-6).
    receivableAccountId: uuid.nullish(),
    roundOffGainAccountId: uuid.nullish(),
    roundOffLossAccountId: uuid.nullish(),
    advanceAccountId: uuid.nullish(),
    refundAccountId: uuid.nullish(),
    depositAccountId: uuid.nullish(),
    // Policy defaults, used when no group-specific mapping row exists.
    defaultRevenueAccountId: uuid.nullish(),
    defaultGstOutputAccountId: uuid.nullish(),
    defaultDiscountAccountId: uuid.nullish(),
    effectiveFrom: isoDate,
    effectiveTo: isoDate.nullish(),
};
exports.createFinPostingProfileSchema = zod_1.z.object(exports.finPostingProfileShape);
exports.updateFinPostingProfileSchema = zod_1.z.object(exports.finPostingProfileShape).partial();
// ---------------------------------------------------------------------------
// Revenue Mapping — the group x profile cell (ADR-081 D-2).
// ---------------------------------------------------------------------------
exports.finRevenueMappingShape = {
    postingGroupId: uuid,
    postingProfileId: uuid,
    /** Null = the tenant-wide default; a branch row overrides it. */
    branchId: uuid.nullish(),
    /** The only required account: a mapping that resolves nothing is not a mapping. */
    revenueAccountId: uuid,
    discountAccountId: uuid.nullish(),
    gstOutputAccountId: uuid.nullish(),
    effectiveFrom: isoDate,
    effectiveTo: isoDate.nullish(),
};
/**
 * `effectiveTo` must not precede `effectiveFrom`.
 *
 * Checked here rather than only in the service because an inverted window is a
 * malformed request, not a business-rule violation — it can never be valid for
 * any tenant, in any configuration.
 */
const orderedWindow = (value, ctx) => {
    const from = value.effectiveFrom;
    const to = value.effectiveTo;
    if (typeof from === 'string' && typeof to === 'string' && new Date(to) < new Date(from)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['effectiveTo'],
            message: 'effectiveTo cannot be earlier than effectiveFrom',
        });
    }
};
exports.createFinRevenueMappingSchema = zod_1.z.object(exports.finRevenueMappingShape).superRefine(orderedWindow);
exports.updateFinRevenueMappingSchema = zod_1.z.object(exports.finRevenueMappingShape).partial().superRefine(orderedWindow);
//# sourceMappingURL=fin-posting-masters.schema.js.map